import { toast } from 'material-react-toastify';

import { coachApi, withAuthHeader } from '@/lib/backend/api';
import { getAuthToken } from '@/lib/firebase/token';
import { coachScheduleKeys } from '@/lib/query/keys/coachScheduleKeys';
import { isSuccess } from '@/lib/utils/response';
import { CoachSchedule } from '@addmile/backend-client';
import * as Sentry from '@sentry/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const defaultSchedule: CoachSchedule = {
  bufferMin: 10,
  sessionDurationMin: 30,
  timeslotDays: 14,
  workingHours: {
    Monday: [],
    Tuesday: [],
    Wednesday: [],
    Thursday: [],
    Friday: [],
    Saturday: [],
    Sunday: [],
  },
};

export const useCoachSchedule = (coachId: string | undefined) => {
  return useQuery<CoachSchedule | undefined>({
    queryKey: coachId ? coachScheduleKeys.detail(coachId) : [],
    queryFn: async () => {
      if (!coachId) throw new Error('Coach ID is required');

      try {
        const token = await getAuthToken();
        const { data: coachSchedule } = await coachApi.getCoachSchedule(
          coachId,
          withAuthHeader(token),
        );
        return coachSchedule;
      } catch (error) {
        Sentry.captureException(`Failed to fetch coach schedule: ${error.message}`);
        toast.error(`Failed to fetch coach schedule: ${error.message}`);
        return undefined;
      }
    },
    enabled: !!coachId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useUpdateCoachSchedule = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ coachId, schedule }: { coachId: string; schedule: CoachSchedule }) => {
      const token = await getAuthToken();
      const response = await coachApi.upsertCoachSchedule(coachId, schedule, withAuthHeader(token));

      if (!isSuccess(response.status)) {
        Sentry.captureException(`Failed to save schedule: ${response.statusText}`);
        toast.error(`Failed to save schedule: ${response.statusText}`);
      }

      return schedule;
    },
    onError: (error) => {
      Sentry.captureException(`Failed to save schedule: ${error.message}`);
      toast.error(`Failed to save schedule: ${error.message}`);
    },
    onSuccess: (updatedSchedule, { coachId }) => {
      queryClient.setQueryData(coachScheduleKeys.detail(coachId), updatedSchedule);
    },
  });
};
